// ShowMore — collapsible wrapper for long-form content.
//
// **Purpose (design-system §2/§4 long-form collapse):** clip content past a
// height threshold and offer a `עוד` toggle. Used at report narratives,
// long detail bodies, and long list overflow.
//
// **Why CSS max-height + measure-on-mount:** keeps the truthful height in
// the DOM (so screen readers / copy-paste read the full text) and avoids
// re-rendering child trees. The collapsed state hides overflow only — no
// `display:none`, no children unmount.
//
// **Threshold:** ~6 lines of body text at 1.5 line-height. Picked from
// design-system; tunable via the `lines` prop.
//
// **No-op below threshold:** if content shorter than the threshold, render
// children directly without the toggle. Cheap measurement is the price.

import { useEffect, useRef, useState } from 'react';

type Props = {
  children: React.ReactNode;
  /** Approximate line count threshold. Defaults to 6 — the design-system §4 long-form rule. */
  lines?: number;
  /** When true, expanded label is shown; otherwise the toggle disappears once expanded. */
  showCollapse?: boolean;
};

const LINE_HEIGHT_REM = 1.5;

export const ShowMore = ({ children, lines = 6, showCollapse = false }: Props) => {
  const wrapper = useRef<HTMLDivElement>(null);
  const [needsClip, setNeedsClip] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Measure on mount + when children change. The clip threshold in pixels
  // approximates lines × line-height; we read root font-size to convert rem.
  useEffect(() => {
    const el = wrapper.current;
    if (!el) return;
    const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const thresholdPx = lines * LINE_HEIGHT_REM * root;
    setNeedsClip(el.scrollHeight > thresholdPx + 4); // +4 to avoid one-pixel triggers
  }, [children, lines]);

  if (!needsClip) {
    return <div ref={wrapper}>{children}</div>;
  }

  const maxHeight = expanded ? undefined : `${lines * LINE_HEIGHT_REM}rem`;
  const showToggle = !expanded || showCollapse;

  return (
    <div>
      <div
        ref={wrapper}
        style={{ maxHeight, overflow: expanded ? undefined : 'hidden' }}
        className="relative"
      >
        {children}
        {!expanded && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-[var(--color-primary-light)] to-transparent"
          />
        )}
      </div>
      {showToggle && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-2 text-xs font-bold text-[var(--color-primary)] hover:underline"
        >
          {expanded ? 'פחות' : 'עוד'}
        </button>
      )}
    </div>
  );
};
